import { GeoJSON } from 'react-leaflet';
import { Feature, FeatureCollection, GeoJsonProperties } from 'geojson';
import { useEffect, useState } from 'react';
import { Coloring, ColoringType, FlatLegendType } from '../../api/api.ts';
import { ColorScaler, getBandColor } from '../../common/coloring.ts';
import { StyleFunction } from 'leaflet';

const { VITE_BACKEND_BASE_URL: BACKEND_BASE_URL } = import.meta.env;

const makeColorObject = (color: string, transparency: number) => {
  return {
    fillColor: color,
    color: color,
    weight: 2,
    fillOpacity: transparency,
  };
};

export default function GeoJsonLayer({
  url,
  coloring,
  property,
}: {
  url: string;
  coloring: Coloring;
  property?: string;
}) {
  const [data, setData] = useState<FeatureCollection | null>();

  useEffect(() => {
    fetch(BACKEND_BASE_URL + url)
      .then((r) => r.json())
      .then((json) => {
        setData(json);
      });
  }, [url]);

  if (
    (coloring.type !== ColoringType.Flat ||
      (coloring.type === ColoringType.Flat &&
        coloring.legend.type === FlatLegendType.TransparencyScale)) &&
    !property
  )
    throw `property must be given for value dependent coloring type ${coloring.type}`;

  const scaler =
    coloring.type === ColoringType.Scale
      ? new ColorScaler(coloring.colors, coloring.min, coloring.max)
      : null;

  const getFeatureStyle:
    | ReturnType<typeof makeColorObject>
    | StyleFunction<GeoJsonProperties> =
    coloring.type === ColoringType.Flat &&
    coloring.legend.type !== FlatLegendType.TransparencyScale
      ? makeColorObject(coloring.color, 0.5)
      : (
          feature?: Feature
        ):
          | {
              fillColor: string;
              color: string;
              weight: number;
              fillOpacity: number;
            }
          | {} => {
          if (!feature) return {};
          const value = Number(
            feature.properties !== null && feature.properties[property!]
          );

          const transparency =
            coloring.type === ColoringType.Flat &&
            coloring.legend.type === FlatLegendType.TransparencyScale
              ? value
              : 0.5;

          switch (coloring.type) {
            case ColoringType.Categories:
              return makeColorObject(
                coloring.colorMap[value].color || coloring.fallback,
                transparency
              );
            case ColoringType.Bands:
              return makeColorObject(
                getBandColor(value, coloring.bands),
                transparency
              );
            case ColoringType.Scale: {
              const [r, g, b] = scaler!.colorize(value);
              return makeColorObject(`rgb(${r}, ${g}, ${b})`, transparency);
            }
            default:
              throw `Unexpected coloring type ${coloring.type}`;
          }
        };

  return data ? <GeoJSON data={data} style={getFeatureStyle} /> : <></>;
}
